import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import 'jspdf-autotable';
import jsPDF from 'jspdf';

declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

type Institution = {
  InstitutionName: string;
};

// Reusable Dropdown Component
const DropdownSelector = ({ items, selectedItems, setSelectedItems, placeholder, width = 240 }) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleItem = item => {
    setSelectedItems(prev =>
      prev.some(i => i.value === item.value)
        ? prev.filter(i => i.value !== item.value)
        : [...prev, item]
    );
  };

  const clearAll = () => setSelectedItems([]);

  return (
    <div
      ref={dropdownRef}
      style={{ position: 'relative', margin: '0 8px 16px 0' }}
    >
      <div
        onClick={() => setDropdownVisible(!dropdownVisible)}
        style={{
          cursor: 'pointer',
          width: `${width}px`,
          height: '42px',
          padding: '10px 12px',
          borderRadius: '6px',
          border: '1px solid #555',
          fontSize: '14px',
          backgroundColor: '#fff',
          color: '#232020ff',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {selectedItems.length > 0 ? selectedItems.map(i => i.label).join(', ') : placeholder}
      </div>

      {dropdownVisible && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: '#a5a8acff',
            border: '1px solid #555',
            borderRadius: '6px',
            padding: '12px',
            zIndex: 100,
            maxHeight: '300px',
            overflowY: 'auto',
            width: `${width}px`,
            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          }}
        >
          <button
            onClick={clearAll}
            style={{
              padding: '6px 12px',
              backgroundColor: '#4a4b4d',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '100%',
              marginBottom: '12px',
            }}
          >
            Clear All
          </button>

          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {items.map(item => (
              <div
                key={item.value}
                onClick={() => toggleItem(item)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px',
                  borderRadius: '4px',
                  backgroundColor: selectedItems.some(i => i.value === item.value)
                    ? '#c5c5cdff'
                    : 'transparent',
                  cursor: 'pointer',
                  marginBottom: '6px',
                  transition: 'background-color 0.2s',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedItems.some(i => i.value === item.value)}
                  readOnly
                  style={{
                    marginRight: '10px',
                    cursor: 'pointer',
                    width: '16px',
                    height: '16px',
                  }}
                />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

type User = {
  user_id: string;
  preferredName: string;
  username: string;
  designation: string;
  UserSignature?: string;
};

interface SearchModalProps {
  user: User | null;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ user, onClose }) => {
  console.log('Received user:', user);
  // State management
  const [filters, setFilters] = useState({
    StudyDateFrom: '',
    StudyDateTo: '',
    PatientName: '',
    PatientAgeFrom: '',
    PatientAgeTo: '',
    PatientSex: '',
    ReportDateFrom: '',
    ReportDateTo: '',
    BodyPartExamined: '',
    StudyDescription: '',
  });

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState([]); // Store all data for client-side filtering

  // Options for dropdowns
  const [institutionOptions, setInstitutionOptions] = useState<{ label: string; value: string }[]>(
    []
  );
  useEffect(() => {
    if (allData.length > 0) {
      const rawInstitutionNames = Array.from(new Set(allData.map(study => study.InstitutionName)));

      const dropdownOptions = rawInstitutionNames.map(name => ({
        label: name,
        value: name,
      }));

      setInstitutionOptions(dropdownOptions);
    }
  }, [allData]);
  const [radiologistOptions, setRadiologistOptions] = useState([]);

  // Selection states
  const [selectedModalities, setSelectedModalities] = useState([]);
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [selectedRadiologists, setSelectedRadiologists] = useState([]);
  const [reportFilter, setReportFilter] = useState('all'); // 'all' | 'reported' | 'no-report'

  // Style constants
  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(69, 61, 61, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const contentStyle = {
    backgroundColor: 'rgba(241, 241, 231, 1)',
    padding: '20px',
    borderRadius: '8px',
    width: '95vw',
    height: '90vh',
    overflow: 'auto',
    position: 'relative',
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  };

  const cellStyle = {
    border: '1px solid #999',
    padding: '8px',
    textAlign: 'left',
  };

  const filterFields = [
    { label: 'Patient Name', name: 'PatientName', type: 'text', width: 280 },
    { label: 'Study Description', name: 'StudyDescription', type: 'text', width: 240 },
    { label: 'Age From', name: 'PatientAgeFrom', type: 'number', width: 80 },
    { label: 'Age To', name: 'PatientAgeTo', type: 'number', width: 80 },
    { label: 'Study Date From', name: 'StudyDateFrom', type: 'date', width: 180 },
    { label: 'Study Date To', name: 'StudyDateTo', type: 'date', width: 180 },
  ];
  // const modalities = ['CT', 'MRI', 'DX', 'CR', 'Ultrasound'];

  const modalities = [
    { label: 'CT', value: 'CT' },
    { label: 'MR', value: 'MR' },
    { label: 'DX', value: 'DX' },
    { label: 'CR', value: 'CR' },
    { label: 'US', value: 'US' },
    { label: 'MammoG', value: 'MG' },
  ];

  // Event handlers
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!allData) return;

    let filteredResults = [];

    if (reportFilter === 'all') {
      filteredResults = allData;
    } else if (reportFilter === 'reported') {
      filteredResults = allData.filter(item => item.reportText && item.reportText.trim() !== '');
    } else if (reportFilter === 'no-report') {
      filteredResults = allData.filter(item => !item.reportText || item.reportText.trim() === '');
    }

    setResults(filteredResults);
  }, [reportFilter, allData]);

  // Apply all filters whenever any filter changes
  useEffect(() => {
    if (allData.length === 0) return;

    // Normalize selected dropdown values
    const selectedModalityValues = selectedModalities.map(m =>
      typeof m === 'string' ? m : m.value
    );
    const selectedInstitutionValues = selectedInstitutions.map(i =>
      typeof i === 'string' ? i : i.value
    );
    const selectedRadiologistValues = selectedRadiologists.map(r =>
      typeof r === 'string' ? r : r.value
    );

    const filtered = allData.filter(entry => {
      // ✅ Modality filter
      if (
        selectedModalityValues.length > 0 &&
        (!entry.Modality ||
          !selectedModalityValues.some(
            m => m.trim().toLowerCase() === entry.Modality.trim().toLowerCase()
          ))
      ) {
        return false;
      }

      // ✅ Institution filter
      if (
        selectedInstitutionValues.length > 0 &&
        (!entry.InstitutionName ||
          !selectedInstitutionValues.some(
            i => i.trim().toLowerCase() === entry.InstitutionName.trim().toLowerCase()
          ))
      ) {
        return false;
      }

      // ✅ Radiologist filter
      if (
        selectedRadiologistValues.length > 0 &&
        (!entry.preferredName ||
          !selectedRadiologistValues.some(
            r => r.trim().toLowerCase() === entry.preferredName.trim().toLowerCase()
          ))
      ) {
        return false;
      }

      // Patient sex filter
      if (
        filters.PatientSex &&
        filters.PatientSex !== 'All' &&
        entry.PatientSex !== filters.PatientSex
      ) {
        return false;
      }

      // Patient name filter
      if (
        filters.PatientName &&
        (!entry.PatientName ||
          !entry.PatientName.toLowerCase().includes(filters.PatientName.toLowerCase()))
      ) {
        return false;
      }

      // Body part filter
      if (
        filters.BodyPartExamined &&
        (!entry.BodyPartExamined ||
          !entry.BodyPartExamined.toLowerCase().includes(filters.BodyPartExamined.toLowerCase()))
      ) {
        return false;
      }

      // Study description filter
      if (
        filters.StudyDescription &&
        (!entry.StudyDescription ||
          !entry.StudyDescription.toLowerCase().includes(filters.StudyDescription.toLowerCase()))
      ) {
        return false;
      }

      // Age filter
      if (filters.PatientAgeFrom || filters.PatientAgeTo) {
        const ageMatch = entry.PatientAge?.match(/(\d+)/);
        const age = ageMatch ? parseInt(ageMatch[1], 10) : null;

        if (age === null) return false;

        if (filters.PatientAgeFrom && age < parseInt(filters.PatientAgeFrom, 10)) {
          return false;
        }

        if (filters.PatientAgeTo && age > parseInt(filters.PatientAgeTo, 10)) {
          return false;
        }
      }

      // Study date filter
      if (filters.StudyDateFrom || filters.StudyDateTo) {
        const studyDate = entry.StudyDate || '';

        if (filters.StudyDateFrom && studyDate < filters.StudyDateFrom.replace(/-/g, '')) {
          return false;
        }

        if (filters.StudyDateTo && studyDate > filters.StudyDateTo.replace(/-/g, '')) {
          return false;
        }
      }

      return true;
    });

    setResults(filtered);
  }, [allData, selectedModalities, selectedInstitutions, selectedRadiologists, filters]);

  useEffect(() => {
    console.log('🟡 useEffect triggered — starting radiologist fetch...');

    const fetchRadiologists = async () => {
      try {
        const radioRes = await fetch('https://studies.clarify-pacs.com/fetchRadiologists.php', {
          credentials: 'include',
        });
        const radioData = await radioRes.json();
        console.log('✅ Radiologists fetched:', radioData);

        // Format for dropdown if needed
        const formatted = radioData.map(name => ({ label: name, value: name }));

        setRadiologistOptions(formatted);
        console.log('📦 Setting radiologist options as:', formatted);
      } catch (error) {
        console.error('❌ Radiologist fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRadiologists();
  }, []);

  // Initial data fetching
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        // Fetch initial results
        const res = await fetch('https://studies.clarify-pacs.com/fetchquerrydb.php', {
          credentials: 'include',
        });
        const data = await res.json();

        if (data.error) {
          console.error('Backend error:', data.error);
          alert('Session expired or not logged in.');
          return;
        }

        console.log('Fetched data:', data);
        setAllData(data);
        setResults(data);

        // Fetch institution options
        const instRes = await fetch('https://studies.clarify-pacs.com/fetchInstitutions.php', {
          credentials: 'include',
        });
        const rawText = await instRes.text();
        console.log('🔍 RAW fetchInstitutions.php response:', rawText);

        try {
          const instData: Institution[] = JSON.parse(rawText);

          const options = instData.map(inst => ({
            label: inst.InstitutionName,
            value: inst.InstitutionName,
          }));
          console.log('✅ Dropdown options:', options);
          setInstitutionOptions(options); // This must be [{label, value}, ...]
        } catch (parseError) {
          console.error('❌ Error parsing institution data:', parseError);
        }
      } catch (error) {
        console.error('Initial data fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleExportToPDF = () => {
    const table = document.querySelector('#filteredTable');
    if (!table) {
      alert('No table found to export.');
      return;
    }

    const doc = new jsPDF();

    doc.text('Patient Data Export', 14, 16);

    // Use autoTable to extract HTML table and style it
    doc.autoTable({
      html: '#filteredTable',
      startY: 20,
      styles: {
        fontSize: 10,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [25, 118, 210], // Blue header
        textColor: [255, 255, 255],
      },
    });

    doc.save('PatientData_Export.pdf');
  };

  const handleExportToExcel = () => {
    const table = document.querySelector('#filteredTable');
    if (!table) {
      alert('No table found to export.');
      return;
    }

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.table_to_sheet(table);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Results');

    XLSX.writeFile(workbook, 'PatientData_Export.xlsx');
  };

  return (
    <div style={modalStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            backgroundColor: '#f0f4f8',
            borderBottom: '1px solid #d0d7de',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <div style={{ flex: 1 }} />
          <h2
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#333',
              marginLeft: '16px',
              marginRight: '16px',
            }}
          >
            Search Patient Database
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#e53935',
              color: '#fff',
              padding: '6px 14px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              transition: 'background-color 0.2s ease-in-out',
            }}
            onMouseOver={e => (e.target.style.backgroundColor = '#c62828')}
            onMouseOut={e => (e.target.style.backgroundColor = '#e53935')}
          >
            Close
          </button>
        </div>

        {/* Filter Section */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: '5px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* Dropdown Selectors */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              padding: '12px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
            }}
          >
            <DropdownSelector
              items={modalities}
              selectedItems={selectedModalities}
              setSelectedItems={setSelectedModalities}
              placeholder="Select Modality"
              width={200}
            />

            <DropdownSelector
              items={institutionOptions}
              selectedItems={selectedInstitutions}
              setSelectedItems={setSelectedInstitutions}
              placeholder="Select Institution"
              width={240}
            />

            <DropdownSelector
              items={radiologistOptions}
              selectedItems={selectedRadiologists}
              setSelectedItems={selected => {
                console.log('🟨 Selected radiologists state:', selected); // <--- Add it here
                setSelectedRadiologists(selected);
              }}
              placeholder="Select Radiologist"
              width={260}
            />
            <div style={{ marginBottom: '1rem' }}>
              <label
                style={{
                  display: 'block',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                  fontSize: '18px',
                  color: '#004085', // Dark blue for the main label
                }}
              >
                Status
              </label>
              <div style={{ display: 'flex', gap: '2rem' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '18px',
                    color: '#1c1e1fff', // Dark gray
                  }}
                >
                  <input
                    type="radio"
                    value="all"
                    checked={reportFilter === 'all'}
                    onChange={e => setReportFilter(e.target.value)}
                  />
                  All Reports
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '18px',
                    color: '#27d427ff', // Dark green
                  }}
                >
                  <input
                    type="radio"
                    value="reported"
                    checked={reportFilter === 'reported'}
                    onChange={e => setReportFilter(e.target.value)}
                  />
                  Reported
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '18px',
                    color: '#922e2eff', // Dark red
                  }}
                >
                  <input
                    type="radio"
                    value="no-report"
                    checked={reportFilter === 'no-report'}
                    onChange={e => setReportFilter(e.target.value)}
                  />
                  No Report
                </label>
              </div>
            </div>
          </div>

          {/* Text/Date Inputs */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              padding: '16px',
              backgroundColor: '#f9f9f9',
              borderRadius: '10px',
              border: '1px solid #ddd',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {filterFields.map((field, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: `${field.width}px`,
                  minWidth: '80px',
                }}
              >
                <label
                  style={{
                    fontWeight: '600',
                    marginBottom: '4px',
                    fontSize: '13px',
                    color: '#333',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {field.label}
                </label>
                <input
                  name={field.name}
                  type={field.type}
                  value={filters[field.name] || ''}
                  onChange={handleInputChange}
                  style={{
                    padding: '6px 8px',
                    fontSize: '13px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Action Bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            flexWrap: 'wrap',
            marginBottom: '5px',
            padding: '10px',
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: '8px',
          }}
        >
          <label style={{ fontWeight: 'bold' }}>Patient Sex:</label>
          {['M', 'F', 'All'].map(sex => (
            <label
              key={sex}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <input
                type="radio"
                name="PatientSex"
                value={sex}
                checked={filters.PatientSex === sex}
                onChange={handleInputChange}
                style={{ width: '18px', height: '18px' }}
              />
              {sex}
            </label>
          ))}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              border: '2px solid #333',
              borderRadius: '6px',
              padding: '8px 16px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ fontWeight: 'bold', minWidth: '160px' }}>
              Results :{' '}
              <span style={{ color: '#190b83ff', fontSize: '20px' }}>{results.length}</span>
            </div>

            <button
              onClick={handleExportToExcel} // Define this handler too
              style={{
                backgroundColor: '#2ecc71',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              Extract to Excel
            </button>
          </div>
        </div>

        {/* Results Table */}
        <div
          style={{
            overflowY: 'auto',
            maxHeight: '900px',
            border: '1px solid #ccc',
            borderRadius: '8px',
            backgroundColor: '#fff',
          }}
        >
          <table
            id="filteredTable"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '1200px',
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: '#2c3e50',
                  color: '#fff',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                }}
              >
                <th style={cellStyle}>Study Date</th>
                <th style={cellStyle}>Patient Name</th>
                <th style={cellStyle}>Report</th>
                <th style={cellStyle}>Radiologist</th>
                <th style={cellStyle}>Modality</th>
                <th style={cellStyle}>Study Description</th>
                <th style={cellStyle}>Institution</th>
                <th style={cellStyle}>Age</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    style={{
                      ...cellStyle,
                      textAlign: 'center',
                      padding: '40px',
                      fontSize: '18px',
                      color: '#666',
                    }}
                  >
                    {loading
                      ? 'Loading data...'
                      : 'No results found. Check network or Login Status.'}
                  </td>
                </tr>
              ) : (
                results.map((item, index) => {
                  console.log('🧾 Row item:', item);
                  return (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#fff',
                      }}
                    >
                      <td style={cellStyle}>{item.StudyDate || '-'}</td>
                      <td style={cellStyle}>{item.PatientName || '-'}</td>
                      <td style={cellStyle}>
                        {item.reportText ? (
                          <a
                            href={`https://studies.clarify-pacs.com/pdfDataFetcher.php?studyInstanceUid=${encodeURIComponent(item.StudyInstanceUID)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View PDF
                          </a>
                        ) : (
                          'No Report'
                        )}
                      </td>
                      <td style={cellStyle}>{item.preferredName || '-'}</td>
                      <td style={cellStyle}>{item.Modality || '-'}</td>
                      <td style={cellStyle}>{item.StudyDescription || '-'}</td>
                      <td style={cellStyle}>{item.InstitutionName || '-'}</td>
                      <td style={cellStyle}>{item.PatientAge || '-'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
